import { ButtonIcon, ButtonLink, IconBookmark } from 'braid-design-system';
import { useTranslations } from '@vocab/react';
import { useCallback } from 'react';

import { useIsSplitViewEnabled } from 'src/store/featureFlags/hooks';
import { useReturnUrl } from 'src/hooks/useReturnUrl';
import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import { useEnrichedLocation } from 'src/modules/enriched-location';
import { linkNavigate } from 'src/store/location/location';
import { useDispatch, useSelector } from 'src/store/react';
import { selectIsJDP, selectSelectedJobId } from 'src/store/selectors';

import translations from './.vocab';

interface SaveJobSectionForSignedOutProps {
  jobId: string;
}
const SaveJobSectionForSignedOut = ({
  jobId,
}: SaveJobSectionForSignedOutProps) => {
  const dispatch = useDispatch();
  const isSplitView = useIsSplitViewEnabled();
  const analyticsFacade = useAnalyticsFacade();

  const { t } = useTranslations(translations);

  const isJDP = useSelector(selectIsJDP);
  const linkPosition = isJDP ? 'job details' : 'listing';

  const signedOutSaveJobHandler = useCallback(() => {
    analyticsFacade.saveJobClicked({ jobId, linkPosition });
  }, [analyticsFacade, jobId, linkPosition]);

  const selectedJobId = useSelector(selectSelectedJobId);

  const returnUrl = useReturnUrl({
    mode: 'signin',
    query: {
      savejob: jobId,
      jobId: selectedJobId,
    },
  });

  // We need to call useEnrichLocation for the linkNavigate action to work correctly
  // TextLink on the other hand will call useEnrichedLocation within NavLink flow
  const enrichedLocation = useEnrichedLocation({
    location: returnUrl,
  });
  return isSplitView ? (
    <ButtonIcon
      id={`serp-signed-out-save-job-${jobId}`}
      label={t('Sign in to save this job')}
      variant="transparent"
      data={{ automation: 'signed-out-save-job' }}
      icon={<IconBookmark />}
      onClick={(event) => {
        dispatch(
          linkNavigate({
            event,
            target: '_self',
            location: enrichedLocation,
            preNavigationHooks: [signedOutSaveJobHandler],
          }),
        );
      }}
    />
  ) : (
    <ButtonLink
      href={returnUrl}
      rel="nofollow"
      aria-label={t('Sign in to save this job')}
      data={{ automation: 'signed-out-save-job' }}
      onClick={signedOutSaveJobHandler}
      tone="neutral"
      variant="transparent"
      bleed
      icon={<IconBookmark />}
    >
      {t('Save')}
    </ButtonLink>
  );
};

export default SaveJobSectionForSignedOut;
